/**
 * WebAuthn helpers for Zitadel's v2 API shapes.
 *
 * Zitadel hands out PublicKeyCredential{Creation,Request}Options as JSON with
 * base64url-encoded binary fields (usually wrapped in `{ publicKey: {...} }`)
 * and expects the browser's credential back the same way. These helpers do
 * the ArrayBuffer <-> base64url conversion in both directions.
 */

export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.PublicKeyCredential &&
    !!navigator.credentials &&
    typeof navigator.credentials.create === "function"
  );
}

export function b64urlToBuf(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function bufToB64url(buf: ArrayBuffer | null): string | null {
  if (!buf) return null;
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const unwrap = (options: any) => (options && options.publicKey ? options.publicKey : options) || {};

/** Register a new credential (security key / platform authenticator). */
export async function createCredential(optionsJson: any): Promise<any> {
  const pk = unwrap(optionsJson);
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...pk,
    challenge: b64urlToBuf(pk.challenge),
    user: { ...pk.user, id: b64urlToBuf(pk.user.id) },
    excludeCredentials: (pk.excludeCredentials || []).map((c: any) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
  };
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error("No credential was created");
  const resp = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      attestationObject: bufToB64url(resp.attestationObject),
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
}

/** Sign a challenge with an existing credential (login second factor). */
export async function getAssertion(optionsJson: any): Promise<any> {
  const pk = unwrap(optionsJson);
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...pk,
    challenge: b64urlToBuf(pk.challenge),
    allowCredentials: (pk.allowCredentials || []).map((c: any) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
  };
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error("No credential was returned");
  const resp = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      authenticatorData: bufToB64url(resp.authenticatorData),
      signature: bufToB64url(resp.signature),
      userHandle: bufToB64url(resp.userHandle),
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
}

/** Friendly text for the DOMExceptions WebAuthn throws. */
export function webauthnErr(err: any, fallback: string): string {
  const name = err?.name || "";
  if (name === "NotAllowedError") return "The request was cancelled or timed out. Try again.";
  if (name === "InvalidStateError") return "This security key is already registered.";
  if (name === "SecurityError") return "Security keys are not available on this address.";
  if (name === "NotSupportedError") return "This browser or device does not support security keys.";
  return err?.message || fallback;
}
